import { createSlice, isAnyOf } from '@reduxjs/toolkit';

import {
  getAllMessages,
  getSelectedMessages,
  getWriters,
  loadMoreWriters,
  getMessageLogs,
  deleteMessage,
  resetSession,
  getScoutFormData,
  getFinalMessages,
  getScreenedMessages,
  replyScout,
  generateMessages,
  selectMessage,
  clearBoard,
  deleteMultipleMessages,
  uploadFile,
  clearAll,
  generateMore,
  compressMessages,
  getVariationScore,
  clearBoardSelected,
  reclaimBackup,
  restoreMessage,
  getBackupMessages,
  likeMessage,
} from './scoutActions';
import {
  ASSISTANTS_CATEGORY,
  REACTIONS,
  SCOUT_ASSISTANTS_CATEGORY,
  SCOUT_CREATE_TYPE,
  SCOUT_TABS,
} from '../../constants';
import {
  getAllMessagesConsumer,
  getConsumerScoutData,
  getSelectedMessagesConsumer,
  getFinalMessagesConsumer,
  getScreenedMessagesConsumer,
  replyScoutConsumer,
  generateMessagesConsumer,
  generateMoreConsumer,
  compressMessagesConsumer,
  resetSessionConsumer,
  deleteMessageConsumer,
  selectMessageConsumer,
  getMessageLogsConsumer,
  deleteMultipleMessagesConsumer,
  consumerScoutFinalMessages,
  getConsumerVariationScore,
  likeMessageConsumer,
} from '../consumer/scout/consumerScoutActions';

const scoutInitialState = {
  generationForm: {
    byPass: '',
    focusOn: '',
    url: '',
    fileText: '',

    channels: {
      [ASSISTANTS_CATEGORY.GENERATE_ALL]: {
        hint: '',
        dbAssistantId: '',
      },
      [ASSISTANTS_CATEGORY.GENERATE_SELECTED]: {
        hint: '',
        dbAssistantId: '',
      },
      [ASSISTANTS_CATEGORY.COMPRESS]: {
        hint: '',
        dbAssistantId: '',
      },
    },
  },
  projectName: '',
  projectId: '',
  assistantsData: {
    [ASSISTANTS_CATEGORY.GENERATE_ALL]: [],
    [ASSISTANTS_CATEGORY.GENERATE_SELECTED]: [],
    [ASSISTANTS_CATEGORY.COMPRESS]: [],
  },
  isFormLoading: false,
  isGenerating: false,
  isResetting: false,
  isFileUploading: false,
  scoutTab: SCOUT_TABS.ALL_MESSAGES,

  isWritersLoading: false,
  writers: [],
  totalWriters: 0,

  isDirty: false,
  editableMessage: {
    id: null,
    message: '',
  },

  messagesContent: [],
  collectDataMessages: [],
  isMessagesLoading: false,

  tableSettings: {
    sort: '-count',
    searchQuery: '',
  },

  messageLogs: {
    histories: [],
    original: {},
  },
  isMessageLogsLoading: false,

  targetNumber: 10,

  variationScore: {},
  isVariationLoading: false,

  finalMessagesCount: 0,
};

const scoutSlice = createSlice({
  name: 'scout',
  initialState: scoutInitialState,
  reducers: {
    changeScoutTab: (state, action) => {
      state.scoutTab = action.payload;
    },

    setFinalMessagesCount: (state, action) => {
      state.finalMessagesCount = action.payload;
    },

    changeLocalMessage: (state, action) => {
      state.messagesContent = state.messagesContent.map((message) =>
        message.id === action.payload.id ? { ...message, message: action.payload.message } : { ...message },
      );
    },
    setIsDirty: (state, action) => {
      state.isDirty = action.payload;
    },
    setEditableMessage: (state, action) => {
      state.editableMessage = action.payload;
    },
    changeEditableMessage: (state, action) => {
      state.editableMessage.message = action.payload;
    },

    changeTableSort: (state, action) => {
      state.tableSettings.sort = action.payload;
      state.tableSettings.page = 0;
    },
    changeTableSearch: (state, action) => {
      state.tableSettings.searchQuery = action.payload;
    },

    likeLocalMessage: (state, action) => {
      if (
        state.scoutTab === SCOUT_TABS.ALL_MESSAGES ||
        state.scoutTab === SCOUT_TABS.SELECTED_MESSAGES ||
        (state.scoutTab === SCOUT_TABS.FINAL_MESSAGES && action.payload.createType === SCOUT_CREATE_TYPE.COMPRESSED)
      ) {
        state.messagesContent = state.messagesContent.map((message) =>
          message.id === action.payload.id ? { ...message, reaction: action.payload.reaction } : { ...message },
        );
        state.finalMessagesCount =
          action.payload.reaction === REACTIONS.LIKE ? state.finalMessagesCount + 1 : state.finalMessagesCount - 1;
      }

      if (state.scoutTab === SCOUT_TABS.FINAL_MESSAGES && action.payload.createType === SCOUT_CREATE_TYPE.GENERATED) {
        state.messagesContent = state.messagesContent.filter((msg) => msg.id !== action.payload.id);
        state.finalMessagesCount = state.finalMessagesCount - 1;
      }

      if (state.scoutTab === SCOUT_TABS.SCREENED_MESSAGES) {
        state.messagesContent = state.messagesContent.filter((msg) => msg.id !== action.payload.id);
        state.finalMessagesCount = state.finalMessagesCount - 1;
      }
    },

    changeTargetNumber: (state, action) => {
      state.targetNumber = action.payload;
    },

    setCollectDataMessages: (state, action) => {
      state.collectDataMessages = action.payload;
    },

    changeGeneratingStatus: (state, action) => {
      state.isGenerating = action.payload;
    },

    resetMessageLogs: (state) => {
      state.messageLogs = scoutInitialState.messageLogs;
    },
    resetScoutCollectDataParams: (state) => {
      state.collectDataMessages = [];
    },
    resetScoutParams: (state) => {
      state.generationForm = scoutInitialState.generationForm;
      state.projectId = scoutInitialState.projectId;
      state.assistantsData = scoutInitialState.assistantsData;
      state.isFormLoading = scoutInitialState.isFormLoading;
      state.isGenerating = scoutInitialState.isGenerating;
      state.isResetting = scoutInitialState.isResetting;
      state.isFileUploading = scoutInitialState.isFileUploading;
      state.scoutTab = scoutInitialState.scoutTab;
      state.isDirty = scoutInitialState.isDirty;
      state.editableMessage = scoutInitialState.editableMessage;
      state.messagesContent = scoutInitialState.messagesContent;
      state.isMessagesLoading = scoutInitialState.isMessagesLoading;
      state.tableSettings = scoutInitialState.tableSettings;
      state.messageLogs = scoutInitialState.messageLogs;
      state.isMessageLogsLoading = scoutInitialState.isMessageLogsLoading;
      state.targetNumber = scoutInitialState.targetNumber;
    },
  },
  extraReducers: (builder) => {
    builder
      //WRITERS
      .addCase(getWriters.fulfilled, (state, action) => {
        state.isWritersLoading = false;
        state.writers = action.payload.content;
        state.totalWriters = action.payload.total;
      })
      .addCase(loadMoreWriters.fulfilled, (state, action) => {
        state.isWritersLoading = false;
        state.writers = [...state.writers, ...action.payload.content];
        state.totalWriters = action.payload.total;
      })

      .addCase(consumerScoutFinalMessages.fulfilled, (state, action) => {
        state.messagesContent = action.payload;
      })

      .addCase(reclaimBackup.fulfilled, (state) => {
        state.isFormLoading = false;
      })

      .addMatcher(isAnyOf(getVariationScore.fulfilled, getConsumerVariationScore.fulfilled), (state, action) => {
        state.variationScore = action.payload;
        state.isVariationLoading = false;
      })
      .addMatcher(isAnyOf(getVariationScore.rejected, getConsumerVariationScore.rejected), (state) => {
        state.isVariationLoading = false;
      })
      .addMatcher(isAnyOf(getVariationScore.pending, getConsumerVariationScore.pending), (state) => {
        state.isVariationLoading = true;
      })

      .addMatcher(isAnyOf(getWriters.pending, loadMoreWriters.pending), (state) => {
        state.isWritersLoading = true;
      })
      .addMatcher(isAnyOf(getWriters.rejected, loadMoreWriters.rejected), (state) => {
        state.isWritersLoading = false;
      })
      .addMatcher(
        isAnyOf(
          getAllMessages.pending,
          getSelectedMessages.pending,
          getFinalMessages.pending,
          getScreenedMessages.pending,
          getBackupMessages.pending,
          deleteMessage.pending,

          getAllMessagesConsumer.pending,
          getSelectedMessagesConsumer.pending,
          getFinalMessagesConsumer.pending,
          getScreenedMessagesConsumer.pending,
          deleteMessageConsumer.pending,
        ),
        (state) => {
          state.isMessagesLoading = true;
        },
      )
      .addMatcher(
        isAnyOf(
          getAllMessages.rejected,
          getSelectedMessages.rejected,
          getFinalMessages.rejected,
          getScreenedMessages.rejected,
          getBackupMessages.rejected,
          deleteMessage.rejected,

          getAllMessagesConsumer.rejected,
          getSelectedMessagesConsumer.rejected,
          getFinalMessagesConsumer.rejected,
          getScreenedMessagesConsumer.rejected,
          deleteMessageConsumer.rejected,
        ),
        (state) => {
          state.isMessagesLoading = false;
        },
      )
      .addMatcher(
        isAnyOf(
          getAllMessages.fulfilled,
          getSelectedMessages.fulfilled,
          getFinalMessages.fulfilled,
          getScreenedMessages.fulfilled,
          getBackupMessages.fulfilled,

          getAllMessagesConsumer.fulfilled,
          getSelectedMessagesConsumer.fulfilled,
          getFinalMessagesConsumer.fulfilled,
          getScreenedMessagesConsumer.fulfilled,
        ),
        (state, action) => {
          state.isMessagesLoading = false;
          state.messagesContent = action.payload;
        },
      )

      .addMatcher(
        isAnyOf(
          getScoutFormData.fulfilled,
          resetSession.fulfilled,
          replyScout.fulfilled,
          generateMessages.fulfilled,
          clearAll.fulfilled,
          generateMore.fulfilled,
          compressMessages.fulfilled,

          getConsumerScoutData.fulfilled,
          replyScoutConsumer.fulfilled,
          generateMessagesConsumer.fulfilled,
          generateMoreConsumer.fulfilled,
          compressMessagesConsumer.fulfilled,
          resetSessionConsumer.fulfilled,
        ),
        (state, action) => {
          state.isFormLoading = false;

          state.generationForm.byPass = action.payload.byPass || '';
          state.generationForm.focusOn = action.payload.focusOn || '';
          state.generationForm.url = action.payload.url || '';
          state.projectName = action.payload.projectName;
          state.projectId = action.payload.id;
          state.assistantsData = action.payload.assistantData;

          const assistantOptions = action.payload.assistantData;

          state.generationForm.channels = SCOUT_ASSISTANTS_CATEGORY.reduce((acc, category) => {
            const chanelData = action.payload.channels[category];

            const defaultAssistantId =
              assistantOptions[category]?.find(({ name }) => name.toLowerCase().includes('default'))?.id ||
              assistantOptions[category][0].id ||
              '';

            acc[category] = {
              hint: chanelData?.hint || '',
              dbAssistantId: chanelData?.dbAssistantId || defaultAssistantId,
            };
            return acc;
          }, {});
        },
      )
      .addMatcher(
        isAnyOf(
          getScoutFormData.pending,
          resetSession.pending,
          // replyScout.pending,
          // generateMessages.pending,
          clearAll.pending,
          generateMore.pending,
          compressMessages.pending,
          reclaimBackup.pending,

          getConsumerScoutData.pending,
          replyScoutConsumer.pending,
          generateMessagesConsumer.pending,
          generateMoreConsumer.pending,
          compressMessagesConsumer.pending,
          resetSessionConsumer.pending,
        ),
        (state) => {
          state.isFormLoading = true;
        },
      )
      .addMatcher(
        isAnyOf(
          getScoutFormData.rejected,
          resetSession.rejected,
          // replyScout.rejected,
          // generateMessages.rejected,
          clearAll.rejected,
          // generateMore.rejected,
          // compressMessages.rejected,
          reclaimBackup.rejected,

          getConsumerScoutData.rejected,
          replyScoutConsumer.rejected,
          generateMessagesConsumer.rejected,
          generateMoreConsumer.rejected,
          compressMessagesConsumer.rejected,
          resetSessionConsumer.rejected,
        ),
        (state) => {
          state.isFormLoading = false;
        },
      )

      .addMatcher(isAnyOf(resetSession.fulfilled, resetSessionConsumer.fulfilled), (state) => {
        state.isResetting = false;
      })
      .addMatcher(isAnyOf(resetSession.pending, resetSessionConsumer.pending), (state) => {
        state.isResetting = true;
      })
      .addMatcher(isAnyOf(resetSession.rejected, resetSessionConsumer.rejected), (state) => {
        state.isResetting = false;
      })

      //DELETE MESSAGE
      .addMatcher(
        isAnyOf(deleteMessage.fulfilled, deleteMessageConsumer.fulfilled, restoreMessage.fulfilled),
        (state, action) => {
          state.isMessagesLoading = false;
          state.messagesContent = state.messagesContent.filter((msg) => msg.id !== action.payload);
        },
      )

      //LIKE MESSAGE
      .addMatcher(isAnyOf(likeMessage.fulfilled, likeMessageConsumer.fulfilled), (state, action) => {
        if (state.scoutTab === SCOUT_TABS.FINAL_MESSAGES) {
          state.messagesContent = state.messagesContent.filter((msg) => msg.id !== action.payload);
        }
      })

      //SELECT MESSAGE
      .addMatcher(isAnyOf(selectMessage.fulfilled, selectMessageConsumer.fulfilled), (state, action) => {
        if (
          state.scoutTab === SCOUT_TABS.ALL_MESSAGES ||
          state.scoutTab === SCOUT_TABS.FINAL_MESSAGES ||
          (state.scoutTab === SCOUT_TABS.SCREENED_MESSAGES &&
            action.payload.createType === SCOUT_CREATE_TYPE.COMPRESSED)
        ) {
          state.messagesContent = state.messagesContent.map((message) =>
            message.id === action.payload.id ? { ...message, selected: action.payload.selected } : { ...message },
          );
        }

        if (
          state.scoutTab === SCOUT_TABS.SELECTED_MESSAGES ||
          (state.scoutTab === SCOUT_TABS.SCREENED_MESSAGES &&
            action.payload.createType !== SCOUT_CREATE_TYPE.COMPRESSED)
        ) {
          state.messagesContent = state.messagesContent.filter((msg) => msg.id !== action.payload.id);
        }
      })

      //MESSAGE LOGS
      .addMatcher(isAnyOf(getMessageLogs.fulfilled, getMessageLogsConsumer.fulfilled), (state, action) => {
        state.messageLogs.histories = action.payload.histories;
        state.messageLogs.original = action.payload.original;
        state.isMessageLogsLoading = false;
      })
      .addMatcher(isAnyOf(getMessageLogs.pending, getMessageLogsConsumer.pending), (state) => {
        state.isMessageLogsLoading = true;
      })
      .addMatcher(isAnyOf(getMessageLogs.rejected, getMessageLogsConsumer.rejected), (state) => {
        state.isMessageLogsLoading = false;
      })

      //UPLOAD FILE
      .addMatcher(isAnyOf(uploadFile.fulfilled), (state) => {
        state.isFileUploading = false;
      })
      .addMatcher(isAnyOf(uploadFile.pending), (state) => {
        state.isFileUploading = true;
      })
      .addMatcher(isAnyOf(uploadFile.rejected), (state) => {
        state.isFileUploading = false;
      })

      //DELETE MESSAGES
      .addMatcher(isAnyOf(deleteMultipleMessages.fulfilled, deleteMultipleMessagesConsumer.fulfilled), (state) => {
        state.isMessagesLoading = false;
      })
      .addMatcher(isAnyOf(deleteMultipleMessages.pending, deleteMultipleMessagesConsumer.pending), (state) => {
        state.isMessagesLoading = true;
      })
      .addMatcher(isAnyOf(deleteMultipleMessages.rejected, deleteMultipleMessagesConsumer.rejected), (state) => {
        state.isMessagesLoading = false;
      })

      //CLEAR BOARD
      .addMatcher(isAnyOf(clearBoard.fulfilled, clearBoardSelected.fulfilled), (state) => {
        state.isMessagesLoading = false;
        state.messagesContent = [];
      })
      .addMatcher(isAnyOf(clearBoard.pending, clearBoardSelected.pending), (state) => {
        state.isMessagesLoading = true;
      })
      .addMatcher(isAnyOf(clearBoard.rejected, clearBoardSelected.rejected), (state) => {
        state.isMessagesLoading = false;
      });
  },
});

export const {
  setIsDirty,
  setEditableMessage,
  changeEditableMessage,
  resetScoutCollectDataParams,
  resetScoutParams,
  setCollectDataMessages,
  changeLocalMessage,
  changeScoutTab,

  changeTableSort,
  changeTableSearch,

  likeLocalMessage,
  hideLocalMessage,
  restoreLocalMessage,

  resetMessageLogs,
  changeContentView,

  changeTargetNumber,

  changeGeneratingStatus,

  setFinalMessagesCount,
} = scoutSlice.actions;

export default scoutSlice.reducer;
